import { authFetch } from '@/api/request';
import { create } from 'zustand';
import {
  listSessions, renameSession as apiRenameSession,
  deleteSession as apiDeleteSession, moveSession as apiMoveSession,
  type SessionListItem,
} from '@/api/modules/sessions';
import { getJobs as apiGetJobs, cancelJob as apiCancelJob, type JobItem } from '@/api/modules/batch';
import {
  getProjects as apiGetProjects, createProject as apiCreateProject,
  deleteProject as apiDeleteProject, updateProject as apiUpdateProject,
  type ProjectData,
} from '@/api/modules/sessions';
import type { Session, Message } from '@/types';
import { useMemo } from 'react';
import { deleteSessionDraft } from '@/utils/session-draft-cache';

/**
 * fetchMessages 竞态序号：快速切换会话时，慢到达的旧响应不得覆盖当前会话。
 * 对齐 agent-web BUG-20260728-005 的 _fetchMessagesSeq 保护。
 */
let _fetchMessagesSeq = 0;

/** 生成配置：随 GDD 确认一并提交给 /chat/stream（对接 P0 后端）。 */
export interface GenConfig {
  generationMode?: 'auto' | 'template' | 'blueprint' | 'cocos' | 'cocos-generation' | 'video';
  imageProvider?: 'jimeng' | 'wanxiang';
  batchConfig?: { count: number; dimensions: Array<{ name: string; values: string[] }> };
  customAssets?: string[];
  referenceImageUrls?: string[];
  skillContent?: string;
  videoFilePaths?: string[];
  endCardFilePaths?: string[];
}

/**
 * 从后端会话元数据恢复 genConfig 时的白名单（fetchMessages 专用）。
 * DO-NOT-REVERT: BUG-20260909-PW-001/003 恢复 genConfig 必须白名单裁剪——后端 gddConfig 可能残留
 * 一次性/内部键（videoFilePaths、旧版误写入的 userImageRoles 等），整体展开回 genConfig 会在下次
 * 发送时随请求回传，触发 ChatDto 400（脏字段，首次微调必失败需重试）或后端把微调误判为新视频
 * 请求（重开素材卡）。只恢复幂等的生成偏好键；videoFilePaths 由配置栏（generation-config-bar）
 * 当轮选择写入，不参与恢复。
 */
const GEN_CONFIG_RESTORE_KEYS = [
  'generationMode', 'imageProvider', 'batchConfig', 'customAssets',
  'referenceImageUrls', 'skillContent', 'endCardFilePaths',
] as const;

/** 白名单裁剪：仅保留 GEN_CONFIG_RESTORE_KEYS 内非 undefined 的键。 */
export function pickGenConfig(raw: object | null | undefined): Partial<GenConfig> {
  const out: Partial<GenConfig> = {};
  if (!raw || typeof raw !== 'object') return out;
  const source = raw as Record<string, unknown>;
  for (const key of GEN_CONFIG_RESTORE_KEYS) {
    const value = source[key];
    if (value !== undefined) (out as Record<string, unknown>)[key] = value;
  }
  return out;
}

// ==================== 会话版本计算 ====================

/** 会话版本：一条用户提问 + 其后第一条修改代码的 assistant 回复。 */
export interface ConversationVersion {
  /** 版本标识，如 "v1"、"v2" */
  id: string;
  /** 版本序号：1, 2, 3... */
  versionNumber: number;
  /** 用户提问消息 ID */
  userMessageId: string;
  /** 用户提问在 messages 数组中的索引 */
  userMessageIndex: number;
  /** 修改代码的 assistant 消息 ID */
  codeResponseId: string;
  /** 修改代码的 assistant 消息在 messages 数组中的索引 */
  codeResponseIndex: number;
  /** 用户提问内容前 50 字符 */
  userPrompt: string;
  /** 预览链接（若可从 metadata 或 content 提取） */
  previewUrl?: string;
  /** assistant 回复的 ISO 时间戳 */
  createdAt: string;
}

/**
 * 判断 assistant 消息是否包含代码修改（即是否构成一个版本边界）。
 * 采用多重 OR 条件，宁可误报不可漏报。
 */
export function hasCodeModification(msg: Message): boolean {
  if (msg.role !== 'assistant') return false;

  const meta = msg.metadata ?? {};
  const content = msg.content ?? '';

  // 1. metadata.buildCard 存在
  if (meta.buildCard) return true;

  // 2. metadata.pipelineStage 包含 prototype_build / 历史 game_develop / assemble / publish
  const pipelineStage = typeof meta.pipelineStage === 'string' ? meta.pipelineStage : '';
  if (pipelineStage && /prototype_build|game_develop|assemble|publish/i.test(pipelineStage)) return true;

  // 3. metadata.toolCalls 数组中任一 tool name 含 assemble / build / publish
  if (Array.isArray(meta.toolCalls)) {
    for (const tc of meta.toolCalls) {
      const name = typeof tc === 'string' ? tc : (tc?.name ?? tc?.tool ?? '');
      if (typeof name === 'string' && /assemble|build|publish/i.test(name)) return true;
    }
  }

  // 4. metadata.nodeOutputs 含 prototype_build / 历史 game_develop / playable_build / assemble 键
  if (meta.nodeOutputs && typeof meta.nodeOutputs === 'object') {
    for (const key of Object.keys(meta.nodeOutputs)) {
      if (/prototype_build|game_develop|playable_build|assemble/i.test(key)) return true;
    }
  }

  // 5. 实际代码库字段：eventType === 'playable:ready'（chat-message BuildCard 判定依据）
  if (meta.eventType === 'playable:ready') return true;

  // 6. metadata.playableReady 为真
  if (meta.playableReady) return true;

  // 7. metadata.previewUrl 为非空字符串（BuildCard 依赖此字段渲染预览）
  if (typeof meta.previewUrl === 'string' && meta.previewUrl.trim()) return true;

  // 8. metadata.isRefinement 为真（微调模式修改代码）
  if (meta.isRefinement) return true;

  // 9. content 含生成完成关键词
  if (/previewUrl|已生成|生成完成|试玩已/.test(content)) return true;

  return false;
}

/**
 * 将消息列表按「版本」分组：每个版本 = 一条用户提问 + 其后第一条修改代码的 assistant 回复。
 * 仅澄清/确认类 assistant 回复不构成版本边界，继续向后搜索直到找到修改代码的回复。
 * 若新的用户提问在配对前出现，则放弃上一条未配对的提问。
 */
export function computeVersions(messages: Message[]): ConversationVersion[] {
  const versions: ConversationVersion[] = [];
  let pendingUser: { msg: Message; index: number } | null = null;
  let versionNumber = 0;

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];

    if (msg.role === 'user') {
      // 新用户提问出现：若上一条提问尚未配对，则放弃它（已被新提问打断）
      pendingUser = { msg, index: i };
      continue;
    }

    if (msg.role === 'assistant' && pendingUser && hasCodeModification(msg)) {
      versionNumber++;
      const userContent = pendingUser.msg.content ?? '';
      const meta = msg.metadata ?? {};

      // 提取 previewUrl：优先 metadata.previewUrl，其次从 content 正则匹配
      let previewUrl: string | undefined;
      if (typeof meta.previewUrl === 'string' && meta.previewUrl.trim()) {
        previewUrl = meta.previewUrl;
      } else {
        const m = msg.content.match(/https?:\/\/[^\s"')]+\.(?:html|zip|h5)/i);
        if (m) previewUrl = m[0];
      }

      versions.push({
        id: `v${versionNumber}`,
        versionNumber,
        userMessageId: pendingUser.msg.id,
        userMessageIndex: pendingUser.index,
        codeResponseId: msg.id,
        codeResponseIndex: i,
        userPrompt: userContent.slice(0, 50),
        previewUrl,
        createdAt: msg.createdAt,
      });
      pendingUser = null;
    }
    // assistant 未修改代码 → 保持 pendingUser，继续向后搜索
  }

  return versions;
}

interface AgentState {
  sessions: Session[];
  /** 会话侧栏列表（含 title/projectId），区别于 SDK Session 实体。 */
  sessionList: SessionListItem[];
  sessionListMeta: { total: number; hasMore: boolean };
  messages: Message[];
  loading: boolean;
  /** 流式回复进行中（从发送到 done/abort）；区别于 loading（首 token 到达即置 false），用于停止按钮展示。 */
  streaming: boolean;
  /** 当前流式生成所属会话 ID；用于会话隔离——切换会话时 UI 不显示其他会话的生成态。BUG-20260729-003 */
  streamingSessionId: string | null;
  messagesLoading: boolean;
  activeSessionId: string | null;
  gddSpec: Record<string, unknown> | null;
  gddConfirmed: boolean;
  /** 流水线事件计数器：SSE 收到 pipeline:stage/pipeline:paused 时自增，流水线组件订阅后主动刷新（解决轮询未启动时状态不更新） */
  pipelineTick: number;
  bumpPipelineTick: () => void;
  /** 微调状态（独立微调图，与生成流水线 pipelineTick/nodeOutputs 完全隔离）：refine:stage/done/failed 事件驱动 */
  refineState: {
    status: 'idle' | 'running' | 'done' | 'failed';
    stage?: 'understand' | 'apply' | 'verify';
    baseVersion?: number;
    basePreviewUrl?: string;
    targetVersion?: number;
    newPreviewUrl?: string;
    playableId?: string;
    error?: string;
    /** 各微调节点输出（refine_understand/refine_apply/refine_verify → {status}），事件原样透传 */
    nodeOutputs?: Record<string, { status: string; error?: string }>;
    /** BUG-20260813-029: 思考过程实时流（refine:thought 事件追加，cap 40），卡片滚动展示避免用户干等 */
    thoughts?: string[];
  };
  setRefineState: (patch: Partial<AgentState['refineState']>) => void;
  /** 预览切换请求（微调完成/回滚时聊天区通知预览区）：ProjectDetail 订阅 tick 变化后 setPreviewUrl(url) */
  previewRequest: { url: string; tick: number } | null;
  requestPreview: (url: string) => void;
  /** 生成配置：随确认提交，随会话切换重置。 */
  genConfig: GenConfig;
  /** 用户已选的选择项（难度/风格等），选中后锁定 */
  choices: Record<string, string>;
  setChoice: (field: string, value: string) => void;
  nodeOutputs: Record<string, { status: string; duration?: number; images?: Array<{url:string;slot:string}>; error?: string }>;
  /** 任务队列（全局，按 sessionId 过滤展示） */
  jobs: JobItem[];
  jobsLoading: boolean;
  /** 项目列表 */
  projects: ProjectData[];
  projectsLoading: boolean;

  setSessions: (sessions: Session[]) => void;
  fetchSessionList: (projectId?: string) => Promise<void>;
  renameSession: (id: string, title: string) => Promise<void>;
  deleteSession: (id: string) => Promise<void>;
  moveSessionToProject: (id: string, projectId: string | null) => Promise<void>;
  setMessages: (messages: Message[]) => void;
  appendMessage: (message: Message) => void;
  updateLastMessage: (content: string) => void;
  finishLastMessage: () => void;
  /** 合并更新指定消息的 metadata（FR-10/11/12：卡片状态消息级持久化） */
  updateMessageMetadata: (msgId: string, patch: Record<string, unknown>) => void;
  setLoading: (loading: boolean) => void;
  /** 设置 streaming 状态；streaming=true 时传入 sessionId 标记当前生成所属会话，false 时自动清除。BUG-20260729-003 */
  setStreaming: (streaming: boolean, sessionId?: string | null) => void;
  setActiveSession: (id: string | null) => void;
  setGddSpec: (spec: Record<string, unknown> | null) => void;
  setGddConfirmed: (confirmed: boolean) => void;
  setGenConfig: (patch: Partial<GenConfig>) => void;
  resetGenConfig: () => void;
  fetchMessages: (sessionId: string) => Promise<any[]>;
  clearMessages: () => void;
  /** 获取任务队列（可传入 sessionId 过滤） */
  fetchJobs: (sessionId?: string) => Promise<void>;
  /** 取消指定任务 */
  cancelJob: (jobId: string) => Promise<void>;
  /** 获取项目列表 */
  fetchProjects: () => Promise<void>;
  /** 创建项目，返回新项目数据 */
  createProject: (name: string) => Promise<ProjectData | null>;
  /** 重命名项目 */
  renameProject: (id: string, name: string) => Promise<void>;
  /** 删除项目 */
  deleteProject: (id: string) => Promise<void>;
}

export const useAgentStore = create<AgentState>((set, get) => ({
  sessions: [],
  sessionList: [],
  sessionListMeta: { total: 0, hasMore: false },
  messages: [],
  loading: false,
  streaming: false,
  streamingSessionId: null,
  messagesLoading: false,
  activeSessionId: null,
  gddSpec: null,
  gddConfirmed: false,
  pipelineTick: 0,
  bumpPipelineTick: () => set((s) => ({ pipelineTick: s.pipelineTick + 1 })),
  refineState: { status: 'idle' },
  setRefineState: (patch) => set((s) => ({ refineState: { ...s.refineState, ...patch } })),
  previewRequest: null,
  requestPreview: (url) => set((s) => ({ previewRequest: { url, tick: (s.previewRequest?.tick ?? 0) + 1 } })),
  genConfig: {},
  choices: {},
  setChoice: (field, value) => set((s) => ({ choices: { ...s.choices, [field]: value } })),
  nodeOutputs: {},
  jobs: [],
  jobsLoading: false,
  projects: [],
  projectsLoading: false,

  setSessions: (sessions) => set({ sessions }),

  fetchSessionList: async (projectId?: string) => {
    try {
      const res = await listSessions({ projectId, limit: 200 });
      // 用户约定（2026-08-26）：永远按创建时间倒序——后端已按 createdAt desc 返回，
      // 前端再排序兜底，防止乐观插入等局部更新打乱顺序（新会话必须始终在最上面）
      const sorted = [...res.sessions].sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
      set({ sessionList: sorted, sessionListMeta: { total: res.total, hasMore: res.hasMore } });
    } catch { /* keep existing list on error */ }
  },

  renameSession: async (id, title) => {
    await apiRenameSession(id, title);
    set((s) => ({ sessionList: s.sessionList.map((x) => (x.id === id ? { ...x, title } : x)) }));
  },

  deleteSession: async (id) => {
    await apiDeleteSession(id);
    await deleteSessionDraft(id);
    set((s) => ({ sessionList: s.sessionList.filter((x) => x.id !== id) }));
  },

  moveSessionToProject: async (id, projectId) => {
    await apiMoveSession(id, projectId);
    // Session left the current project view → drop from list; a refetch will reconcile.
    set((s) => ({ sessionList: s.sessionList.map((x) => (x.id === id ? { ...x, projectId } : x)) }));
    await get().fetchSessionList(projectId ?? undefined);
  },

  setMessages: (messages) => set({ messages }),
  appendMessage: (message) => set((s) => ({ messages: [...s.messages, message] })),
  updateLastMessage: (content: string) =>
    set((s) => {
      const msgs = [...s.messages];
      if (msgs.length > 0) {
        msgs[msgs.length - 1] = { ...msgs[msgs.length - 1], content };
      }
      return { messages: msgs };
    }),
  finishLastMessage: () =>
    set((s) => {
      const msgs = [...s.messages];
      if (msgs.length > 0) {
        msgs[msgs.length - 1] = { ...msgs[msgs.length - 1], metadata: { ...msgs[msgs.length - 1].metadata, streaming: false } };
      }
      return { messages: msgs };
    }),
  updateMessageMetadata: (msgId, patch) =>
    set((s) => ({
      messages: s.messages.map((m) => (m.id === msgId ? { ...m, metadata: { ...m.metadata, ...patch } } : m)),
    })),
  setLoading: (loading) => set({ loading }),
  // DO-NOT-REVERT: BUG-20260729-003 streaming 必须按会话隔离，streaming=true 时记录 sessionId，false 时清除
  setStreaming: (streaming, sessionId) => set((s) => ({
    streaming,
    streamingSessionId: streaming ? (sessionId ?? s.streamingSessionId) : null,
  })),
  setActiveSession: (id) => set({ activeSessionId: id }),
  setGddSpec: (spec) => set({ gddSpec: spec }),
  setGddConfirmed: (confirmed) => set({ gddConfirmed: confirmed }),
  setGenConfig: (patch) => set((s) => ({ genConfig: { ...s.genConfig, ...patch } })),
  resetGenConfig: () => set({ genConfig: {} }),

  fetchMessages: async (sessionId: string) => {
    // 竞态保护：记录本次请求序号，响应到达时若已被更晚的请求超越则整体丢弃（BUG-20260728-005）。
    const seq = ++_fetchMessagesSeq;
    set({ messagesLoading: true });
    try {
      // 单次 fetch 同时获取 messages + metadata + playables
      const sRes = await authFetch(`/api/agent/session/${sessionId}`).then(r => r.json()).catch(() => ({}));
      const sessionData = sRes?.data ?? {};
      const msgs = sessionData.messages ?? [];
      const meta = sessionData.metadata ?? {};
      const playables = sessionData.playables ?? [];
      if (seq !== _fetchMessagesSeq) return; // 会话已切换，丢弃旧结果

      // BUG-20260827-WEB-003: 刷新恢复——对话进行中 liveActivity 尚未落 assistant 消息时，合成临时活动气泡
      const md = typeof meta === 'string' ? JSON.parse(meta) : meta;
      const liveActivity = Array.isArray(md?.liveActivity) ? md.liveActivity as Array<{ type: string; data: Record<string, unknown> }> : [];
      const pipelineState = md?.pipelineState as { status?: string } | undefined;
      const pipelineRunning = pipelineState?.status === 'running' || pipelineState?.status === 'paused';

      // 历史会话 metadata 偶尔会是旧版本/截断 JSON；解析失败不能让整个消息列表被清空。
      let safeMd: Record<string, any> = {};
      try {
        safeMd = (typeof meta === 'string' ? JSON.parse(meta) : meta) ?? {};
      } catch {
        safeMd = {};
      }

      // Restore GDD from saved messages
      let gddDraft: Record<string, unknown> | null = null;
      for (const m of msgs) {
        if (m.role !== 'assistant') continue;
        const gddMatch = String(m.content ?? '').match(/<GDD>([\s\S]*?)<\/GDD>/i);
        if (gddMatch) try { gddDraft = JSON.parse(gddMatch[1].trim()); } catch {}
      }
      const restoredSpec = gddDraft ?? (safeMd.gddDraft as Record<string, unknown> | undefined) ?? null;
      // 会话切换时没有 GDD 也要显式清空旧会话的 spec，避免文档标题/发布名称短暂串台。
      set({ gddSpec: restoredSpec });

      // 将会话级 gddDraft 回填到对应 assistant 消息
      let gddMessageIndex = -1;
      for (let i = msgs.length - 1; i >= 0; i--) {
        const m = msgs[i];
        if (m.role === 'assistant' && (
          /<GDD/i.test(m.content ?? '')
          || (m.metadata?.gddSpec && typeof m.metadata.gddSpec === 'object')
          || m.metadata?.gddEvent
        )) {
          gddMessageIndex = i;
          break;
        }
      }
      if (gddMessageIndex < 0 && restoredSpec) {
        for (let i = msgs.length - 1; i >= 0; i--) {
          if (msgs[i].role === 'assistant') { gddMessageIndex = i; break; }
        }
      }
      let restoredMessages = restoredSpec && gddMessageIndex >= 0
        ? msgs.map((m, i) => i === gddMessageIndex
          ? { ...m, metadata: { ...m.metadata, gddSpec: restoredSpec, gddEvent: true } }
          : m)
        : msgs;

      // liveActivity 恢复：流水线进行中且末条为用户消息时，合成临时活动气泡
      const lastMsg = restoredMessages[restoredMessages.length - 1];
      if (liveActivity.length > 0 && lastMsg?.role === 'user') {
        restoredMessages = [
          ...restoredMessages,
          {
            id: `live-activity-${sessionId}`,
            sessionId,
            role: 'assistant' as const,
            content: '',
            metadata: {
              streaming: true,
              activity: liveActivity.map((e) => ({ type: e.type, data: e.data })),
              liveRecovery: true,
            },
            createdAt: new Date().toISOString(),
          },
        ];
        set({ messages: restoredMessages, streaming: true, streamingSessionId: sessionId });
      } else {
        set({ messages: restoredMessages });
      }

      const savedGenConfig = (safeMd?.gddConfig ?? {}) as GenConfig;
      // 旧会话可能还没有 gddConfig；用最近一次本地选择兜底，但后端会话值始终优先。
      const localProvider = typeof window !== 'undefined' ? window.localStorage.getItem('playgen:image-provider:last') : null;
      const imageProvider = savedGenConfig.imageProvider
        ?? (localProvider === 'jimeng' || localProvider === 'wanxiang' ? localProvider : 'jimeng');
      // BUG-20260909-PW-001/003: 恢复 genConfig 必须白名单裁剪（pickGenConfig）——后端元数据
      // 残留的 videoFilePaths/旧版 userImageRoles 若整体展开，下次微调/发送会随请求回传，
      // 触发 400 或 handleVideoDirect 误判重开流水线（首次微调必失败 + 黑屏的根因链路）
      set({ genConfig: { ...pickGenConfig(savedGenConfig), imageProvider } });
      set({ gddConfirmed: Boolean(safeMd?.gddConfirmed || (safeMd?.nodeOutputs && Object.keys(safeMd.nodeOutputs).length > 0)) });
      // 流水线 running/paused 时 bump tick，驱动 usePipelinePoll 立即拉节点（刷新后恢复进度）
      if (pipelineRunning) {
        useAgentStore.getState().bumpPipelineTick();
      }

      // 返回 playables 给调用方（避免 loadSession 再调一次）
      return playables;
    } catch {
      if (seq !== _fetchMessagesSeq) return [];
      // 保留当前已展示的消息；网络抖动/旧 metadata 解析异常不应让游戏文档瞬间消失。
      return [];
    } finally {
      if (seq === _fetchMessagesSeq) set({ messagesLoading: false });
    }
  },

  clearMessages: () => set({ messages: [], gddSpec: null, gddConfirmed: false, genConfig: {}, choices: {} }),

  fetchJobs: async (sessionId?: string) => {
    set({ jobsLoading: true });
    try {
      const res = await apiGetJobs(sessionId);
      set({ jobs: res.data ?? [] });
    } catch {
      set({ jobs: [] });
    } finally {
      set({ jobsLoading: false });
    }
  },

  cancelJob: async (jobId: string) => {
    try {
      await apiCancelJob(jobId);
      set((s) => ({ jobs: s.jobs.filter((j) => j.jobId !== jobId) }));
    } catch { /* ignore */ }
  },

  fetchProjects: async () => {
    set({ projectsLoading: true });
    try {
      const res = await apiGetProjects();
      set({ projects: res.data ?? [] });
    } catch {
      set({ projects: [] });
    } finally {
      set({ projectsLoading: false });
    }
  },

  createProject: async (name: string) => {
    try {
      const res = await apiCreateProject(name);
      const project = res.data;
      set((s) => ({ projects: [...s.projects, project] }));
      return project;
    } catch {
      return null;
    }
  },

  renameProject: async (id: string, name: string) => {
    await apiUpdateProject(id, { name });
    set((s) => ({ projects: s.projects.map((p) => (p.id === id ? { ...p, name } : p)) }));
  },

  deleteProject: async (id: string) => {
    await apiDeleteProject(id);
    set((s) => ({ projects: s.projects.filter((p) => p.id !== id) }));
  },
}));

/**
 * 选择器 hook：从 store messages 派生会话版本列表，经 useMemo 记忆化。
 * messages 引用变更（追加/更新消息）时自动重算。
 */
export function useConversationVersions(): ConversationVersion[] {
  const messages = useAgentStore((s) => s.messages);
  return useMemo(() => computeVersions(messages), [messages]);
}
